import { type Role, userCreate, userUpdate } from '@trafiku/contracts';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Field, Input, Select, Switch } from '@/components/ui/form';
import { Badge, Card, PageHeader } from '@/components/ui/layout';
import { DataTable } from '@/components/ui/table';
import { useAuth } from '@/lib/auth';
import { useFormState } from '@/lib/form';
import { formatDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useList, useRemove, useSave } from '@/lib/resources';
import type { AdminUser } from '@/lib/types';

function UserDialog({ user, onClose }: { user: AdminUser | 'new'; onClose: () => void }) {
  const isNew = user === 'new';
  const save = useSave<AdminUser>('users', isNew ? undefined : user._id);
  const remove = useRemove('users');
  const { user: me } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const form = useFormState({
    email: isNew ? '' : user.email,
    password: '',
    role: (isNew ? 'operator' : (user.role ?? 'operator')) as Role,
    is_active: isNew ? true : user.is_active,
  });
  const { values, set, errors } = form;
  const submit = async () => {
    const ok = isNew
      ? await form.submit(userCreate, (d) => save.mutateAsync(d))
      : await form.submit(userUpdate, (d) =>
          save.mutateAsync({
            role: d.role,
            is_active: d.is_active,
            ...(values.password ? { password: d.password } : {}),
          }),
        );
    if (ok) onClose();
  };
  const self = !isNew && user._id === me?.id;
  return (
    <>
      <Dialog
        open
        onClose={onClose}
        title={isNew ? t('New admin user') : user.email}
        footer={
          <>
            {!isNew && !self && (
              <Button
                variant="ghost"
                className="mr-auto text-red-600! hover:bg-red-50"
                onClick={() => setConfirm(true)}
              >
                {t('Delete')}
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {isNew ? t('Create') : t('Save')}
            </Button>
          </>
        }
      >
        {isNew && (
          <Field label={t('E-mail')} error={errors.email}>
            {(fid) => (
              <Input
                id={fid}
                type="email"
                value={values.email}
                onChange={(e) => set('email', e.target.value)}
              />
            )}
          </Field>
        )}
        <Field
          label={isNew ? t('Password') : t('New password (optional)')}
          error={errors.password}
          hint={t('At least {n} characters', { n: 10 })}
        >
          {(fid) => (
            <Input
              id={fid}
              type="password"
              autoComplete="new-password"
              value={values.password}
              onChange={(e) => set('password', e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('Role')}
          hint={t(
            'Operators manage buses, timetables, news, reports and notifications. Admins can do everything.',
          )}
        >
          {(fid) => (
            <Select
              id={fid}
              value={values.role}
              onChange={(e) => set('role', e.target.value as Role)}
              disabled={self}
            >
              <option value="operator">{t('Operator')}</option>
              <option value="admin">{t('Admin')}</option>
            </Select>
          )}
        </Field>
        {!self && (
          <Switch checked={values.is_active} onChange={(v) => set('is_active', v)} label={t('Can sign in')} />
        )}
      </Dialog>
      <ConfirmDialog
        open={confirm}
        onCancel={() => setConfirm(false)}
        title={t('Delete user?')}
        message={t('They will be signed out immediately.')}
        loading={remove.isPending}
        onConfirm={() => !isNew && remove.mutate(user._id, { onSuccess: onClose })}
      />
    </>
  );
}

export function UsersPage() {
  const list = useList<AdminUser>('users', { pageSize: 200 });
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);
  return (
    <>
      <PageHeader
        title={t('Admin users')}
        actions={
          <Button icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
            {t('New user')}
          </Button>
        }
      />
      <Card padded={false}>
        <DataTable
          rows={list.data?.items}
          loading={list.isFetching}
          columns={[
            {
              header: t('E-mail'),
              cell: (u) => (
                <button type="button" className="font-medium hover:underline" onClick={() => setEditing(u)}>
                  {u.email}
                </button>
              ),
            },
            {
              header: t('Role'),
              cell: (u) =>
                u.role ? (
                  <span>{u.role === 'admin' ? t('Admin') : t('Operator')}</span>
                ) : (
                  <Badge tone="red">{t('No access')}</Badge>
                ),
            },
            {
              header: t('Status'),
              cell: (u) =>
                u.is_active ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Disabled')}</Badge>,
            },
            { header: t('Created'), cell: (u) => formatDate(u.created_at) },
          ]}
        />
      </Card>
      {editing && <UserDialog user={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
